<template>
  <PageTemplate>
    <h1 v-text="title" />
    <div class="row">
      <div class="col-lg-4 d-flex">
        <v-card title="Site Details">
          <v-definition-list
            :data="siteDetails"
          />
        </v-card>
      </div>
      <div class="col-lg-4 d-flex">
        <v-card title="Contact Details">
          <template #header>
            <router-link
              to="/contact"
            >
              <v-icon type="edit" />
              Edit
            </router-link>
          </template>
          <v-definition-list
            :data="contactDetails"
          />
        </v-card>
      </div>
      <div class="col-lg-4 d-flex">
        <v-card title="Accreditation Specialist">
          <v-definition-list
            :data="clientCoordinator"
          />
        </v-card>
      </div>
    </div>

    <v-card title="Jobs">
      <v-table
        :table="job"
      />
    </v-card>
    <v-tabs>
      <v-tab
        name="Received Site Documents"
        :selected="true"
      >
        <v-table
          ref="receivedDocumentTable"
          :table="received"
          :checkboxes-enabled="true"
          :sort-options="{
            enabled: true,
            initialSortBy: {field: 'dateUploaded', type: 'asc'}
          }"
          @showDoc="previewDoc"
          @downloadDoc="downloadDoc"
        >
          <template #optional>
            <v-button
              color="primary"
              @click="downloadSelectedReceived"
            >
              Download Selected
            </v-button>
          </template>
        </v-table>
      </v-tab>
      <v-tab name="Submitted Site Documents">
        <v-table
          ref="submittedDocumentTable"
          :table="submitted"
          :checkboxes-enabled="true"
          :sort-options="{
            enabled: true,
            initialSortBy: {field: 'dateUploaded', type: 'asc'}
          }"
          @showDoc="previewDoc"
          @downloadDoc="downloadDoc"
        >
          <template #optional>
            <v-button
              color="primary"
              @click="downloadSelectedSubmitted"
            >
              Download Selected
            </v-button>
          </template>
        </v-table>
        <v-button
          size="lg"
          @click="openUploadModal"
        >
          Upload Document
        </v-button>
      </v-tab>
    </v-tabs>

    <v-modal
      v-if="showEditModal"
      @close="closeEditModal"
    >
      <template #header>
        Update Contact Details
      </template>
      <template v-for="col in editData.editableCols">
        {{ col.key.toUpperCase() }}
        <v-input
          :key="col.key"
          :name="col.key"
          :value="col.value"
          @input="changeColValue"
        />
      </template>
      <template #footer>
        <v-button-group is-right>
          <v-button
            v-if="dataIsChanged"
            size="lg"
            has-min-width
            @click="commitUpdateData"
          >
            Update
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeEditModal"
          >
            Close
          </v-button>
        </v-button-group>
      </template>
    </v-modal>

    <v-modal
      v-if="showUploadModal"
      @close="closeUploadModal"
    >
      <template #header>
        File Upload
      </template>
      <template v-if="file">
        <div
          v-if="fileConfirmed"
          class="row align-items-center"
        >
          <div class="col-lg-6">
            <v-fieldset
              v-model="uploadDescription"
              type="textarea"
              text="Details/Report Reference*"
              maxlength="255"
              @input="inputChange"
            />
            <div
              class="max-char-text"
            >
              (*mandatory field, 255 character maximum.)
            </div>
          </div>
          <div class="col-lg-6">
            <v-fileupload
              v-if="file"
              :file-name="file.name"
              :file-size="file.size | formatFileSize"
              @clear="fileRemoved"
            />
          </div>
          <v-spinner
            v-if="isUploading"
            :is-full="false"
          />
        </div>
        <div
          v-else
          class="text--center"
        >
          <p class="text--lg">
            Are you sure this is the file you want to upload to {{ title }}?
          </p>
          <p class="text--lg">
            <strong>{{ file.name }}</strong> {{ file.size | formatFileSize }}
          </p>
          <v-button-group is-inline>
            <v-button
              v-if="file"
              has-min-width
              @click="fileConfirmed = true"
            >
              Yes
            </v-button>
            <v-button
              color="secondary"
              has-min-width
              @click="fileRemoved"
            >
              No
            </v-button>
          </v-button-group>
        </div>
      </template>
      <v-dropzone
        v-else
        additional-msg="(Special characters (!@#$%^&*_+/) must not be used in filenames. Multiple files are best uploaded as a zipped file folder.)"
        @file-added="fileAdded"
        @file-error="uploadError"
      />
      <template #footer>
        <v-button-group is-right>
          <v-button
            v-if="file && fileConfirmed"
            size="lg"
            has-min-width
            @click="uploadDocument"
          >
            Finish
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeUploadModal"
          >
            Cancel
          </v-button>
        </v-button-group>
      </template>
    </v-modal>
  </PageTemplate>
</template>
<script>
  import PageTemplate from "../templates/Page/index.vue";
  import Fieldset from "@/molecules/Fieldset";
  import Card from "@/molecules/Card";
  import Table from "@/molecules/Table";
  import Tabs from "@/molecules/Tabs";
  import Tab from "@/atoms/Tab";
  import Button from "@/atoms/Button";
  import Icon from "@/atoms/Icon";
  import Spinner from "@/atoms/Spinner";
  import DefinitionList from "@/atoms/DefinitionList";
  import Modal from "@/atoms/Modal";
  import Dropzone from "@/molecules/Dropzone";
  import ButtonGroup from "@/molecules/ButtonGroup";
  import FileUpload from '@/molecules/FileUpload'
  import {
    GET_SITE,
    GET_SITE_DOCUMENTS,
    UPDATE_SITE,
    UPLOAD_SITE_DOCUMENT,
    PREVIEW_SITE_DOCUMENT,
    DOWNLOAD_SITE_DOCUMENT,
    DOWNLOAD_SITE_DOCUMENTS
  } from "@/store/actions.type";
  import Input from "@/atoms/Input";
  import flatPicker from "flatpickr";
  import "flatpickr/dist/flatpickr.css";
  import "flatpickr/dist/themes/airbnb.css";
  import {
    columnFilter,
    columnSort,
    buildBreadCrumb,
    buildDefinitionList,
    dateSort_ddmmyyyy
  } from "../common/tableFunctions";
  import {formatFileSize, formatDate} from "../common/formatting";
  import constants from "../common/constants";

  export default {
    name: "Site",
    components: {
      "v-table": Table,
      "v-fieldset": Fieldset,
      "v-tabs": Tabs,
      "v-tab": Tab,
      "v-definition-list": DefinitionList,
      "v-card": Card,
      "v-icon": Icon,
      "v-button": Button,
      "v-modal": Modal,
      "v-button-group": ButtonGroup,
      "v-dropzone": Dropzone,
      "v-input": Input,
      "v-spinner": Spinner,
      'v-fileupload': FileUpload,
      PageTemplate,
    },
    filters: {
      formatFileSize: formatFileSize
    },
    data() {
      return {
        components: {
          flatPicker
        },
        editableCols: {
          key: 'site_id',
          cols: ['phone', 'email', 'address', 'suburb', 'state']
        },
        job: {
          columns: [
            {
              label: 'data',
              field: 'data',
              hidden: true
            },
            {
              label: 'Job Number',
              field: 'job_number',
              link: true,
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Job ID',
                filterFn: columnFilter
              },
            },
            {
              label: 'Job Type',
              field: 'type',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'All',
                filterDropdownItems: ['RES', 'Other'],
                filterFn: columnFilter
              },
            },
            {
              label: 'Status',
              field: 'status',
              badge: true,
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'All',
                filterDropdownItems: ['In progress', 'Planned', 'Complete', 'Cancel'],
                filterFn: columnFilter
              },
            },
            {
              label: 'Stage',
              field: 'stage',
              badge: true,
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'All',
                filterDropdownItems: ['Initiation', 'Assessment', 'Preparation', 'Submission', 'Decision'],
                filterFn: columnFilter
              },
            },
            {
              label: 'Visit Date',
              field: 'visit_date',
              sortFn: dateSort_ddmmyyyy,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Date',
                filterFn: columnFilter
              },
            },
            {
              label: 'Lead Assessor Name',
              field: 'lead_assessor_name',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Lead Assessor Name',
                filterFn: columnFilter
              },
            },
            {
              label: 'Lead Assessor Email',
              field: 'lead_assessor_email',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Lead Assessor Email',
                filterFn: columnFilter
              },
            },
          ],
          rows: [],
          totalRecords: 0
        },
        received: {
          columns: [
            {
              label: 'data',
              field: 'data',
              hidden: true
            },
            {
              label: 'Document Name',
              field: 'docName',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Enter Document Name',
                filterFn: columnFilter
              },
            },
            {
              label: 'Description',
              field: 'desc',
              sortable: false,
              thClass: 'shrink-cell',

            },
            {
              label: 'Date Uploaded',
              field: 'dateUploaded',
              sortFn: dateSort_ddmmyyyy,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Date',
                filterFn: columnFilter
              },
            },
            {
              label: 'Actions',
              field: 'actions',
              sortable: false,
              actions: 'view_and_download',
              thClass: 'shrink-cell',
              tdClass: 'no-wrap',
            },
          ],
          rows: [],
          totalRecords: 0
        },
        submitted: {
          columns: [
            {
              label: 'Document Name',
              field: 'docName',
              sortFn: columnSort,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Enter Document Name',
                filterFn: columnFilter
              },
            },
            {
              label: 'Description',
              field: 'desc',
              sortable: false,
              thClass: 'shrink-cell'
            },
            {
              label: 'Date Uploaded',
              field: 'dateUploaded',
              sortFn: dateSort_ddmmyyyy,
              thClass: 'sortable',
              filterOptions: {
                enabled: true,
                placeholder: 'Date',
                filterFn: columnFilter
              },
            },
            {
              label: 'Uploaded By',
              field: 'uploadedBy',
              sortable: false,
              thClass: 'shrink-cell',
              filterOptions: {
                enabled: true,
                placeholder: 'Name',
                filterFn: columnFilter
              },
            },
            {
              label: 'Actions',
              field: 'actions',
              sortable: false,
              actions: 'view_and_download',
              thClass: 'shrink-cell',
              tdClass: 'no-wrap',
            },
          ],
          rows: [],
          totalRecords: 0
        },
        title: null,
        breadcrumbs: [],
        siteDetails: [],
        contactDetails: [],
        clientCoordinator: [],
        showUploadModal: false,
        modalHasFile: false,
        showEditModal: false,
        editData: {},
        editedData: {},
        actionStatus: false,
        actionMsg: {},
        dataIsChanged: false,
        uploadDescription: "",
        file: null,
        isUploading: false,
        fileConfirmed: false
      };
    },
    computed: {
      siteDocuments() {
        return this.$store.state.siteDocuments.siteDocuments;
      },
    },
    watch: {
      actionMsg() {
        if (this.actionMsg.success) {
          this.actionStatus = true;
          this.dataIsChanged = false;
          this.$notify({
            title: 'Success',
            text: 'Action successful!',
            type: 'success'
          });
        }
        else {
          let errors = this.actionMsg.data;
          let errorMsg = '';

          errorMsg = Object.keys(errors).map(function (key) {
            return errorMsg.concat(key, ':', errors[key][0], ';')
          });
          this.$notify({
            title: 'Error',
            text: errorMsg,
            type: 'error'
          });
        }
      }
    },
    created() {
      this.showEditModal = false;
      this.actionStatus = false;
    },
    mounted() {
      this.getSite(this.$route.params.id);
      let inputs = [
        'input[placeholder="Date"]',
      ];
      inputs.forEach(function (input) {
        flatPicker(input, {
          dateFormat: "d/m/Y",
          allowInput: true,
          theme: "light",
          disableMobile: true
        });
      });
      this.getDocuments(this.$route.params.id);
    },
    methods: {
      getDocuments: function (siteId) {
        this.$store.dispatch(GET_SITE_DOCUMENTS, {siteId}).then(response => {
          this.received.rows = this.siteDocuments.received;
          this.submitted.rows = this.siteDocuments.submitted;
        });
      },
      updateBreadCrumb: function(fn, fnum, fid, siteNum, siteTitle) {
        let breadcrumbs = [];
        breadcrumbs['Dashboard'] = '/dashboard';
        breadcrumbs['Facilities/Sites'] = '/facilities';
        breadcrumbs[fn + ' - ' + fnum] = '/facility/' + fid;
        breadcrumbs[siteTitle + ' - ' + siteNum] = '';
        this.$store.dispatch('updateBreadCrumbAction', buildBreadCrumb(breadcrumbs));
      },
      getSite: function (site_id) {
        this.$store.dispatch(GET_SITE, {site_id}).then(response => {
          let res = response.data;

          this.title = res.name;
          this.siteId = res.id;
          this.updateBreadCrumb(res.facility.name, res.facility.facility_number, res.facility.id, res.site_number, this.title);
          let siteDetails = [];
          let contactDetails = [];
          let clientCoordinator = [];

          siteDetails['Facility Name'] = res.facility.name;
          siteDetails['Facility Number'] = res.facility.facility_number;
          siteDetails['Site Name'] = res.name;
          siteDetails['Site Number'] = res.site_number;
          siteDetails['Site Status'] = res.status;
          this.siteDetails = buildDefinitionList(siteDetails);

          let phone = {};
          let contactsObj = JSON.parse(res.contacts);
          let contact = contactsObj.contacts[0];
          if (contact) {
            phone.value = contact.phone;
            contactDetails['Phone'] = res.phone;
          }
          if (res.email != undefined) {
            contactDetails['Email'] = res.email;
          }
          if (res.address != undefined) {
            contactDetails['Address'] = res.address;
          }
          if (res.suburb != undefined) {
            contactDetails['Suburb'] = res.suburb;
          }
          if (res.post_code != undefined) {
            contactDetails['Post Code'] = res.post_code;
          }
          if (res.state != undefined) {
            contactDetails['State'] = res.country !== null ? (res.state + ', ' + res.country) : (res.state);
          }
          this.contactDetails = buildDefinitionList(contactDetails);

          let cc = JSON.parse(res.client_coordinator);
          if (cc !== undefined && cc !== null) {
                clientCoordinator['Name'] = cc.first_name + ' ' + cc.last_name;
                clientCoordinator['Phone'] = cc.phone;
                clientCoordinator['Email'] = cc.email;
            } else {
                clientCoordinator['Name'] = '';
                clientCoordinator['Phone'] = '';
                clientCoordinator['Email'] = '';
            }
          this.clientCoordinator = buildDefinitionList(clientCoordinator);

          this.job.rows = this.formatJobs(res.jobs);

          //filter unique status
          const arr = this.job.rows.map(i => i.status.value);
          this.job.columns[3].filterOptions.filterDropdownItems = Array.from([...new Set(arr.filter(
            function (el) {
              return el != '-';
            }
          ))]);

          // //filter unique state
          const arr2 = this.job.rows.map(i => i.stage.value);
          this.job.columns[4].filterOptions.filterDropdownItems = Array.from([...new Set(arr2.filter(
            function (el) {
              return el != '-';
            }
          ))]);

          //filter unique job type
          const arr3 = this.job.rows.map(i => i.type.value);
          this.job.columns[2].filterOptions.filterDropdownItems = Array.from([...new Set(arr3.filter(
            function (el) {
              return el != '-';
            }
          ))]);
        });
      },
      inputChange(val) {
        this.uploadDescription = val;
      },
      downloadDoc(params) {
        this.$store.state.auth.isLoading = true;
        let url = params;
        this.$store.dispatch(DOWNLOAD_SITE_DOCUMENT, {url})
          .then(response => {
            return response;
          }).finally(() => {
            this.$store.state.auth.isLoading = false;
          });
      },
      downloadSelected(rows) {
        let selectedIds = rows.map( function (row) {
          return row.data.id;
        });
        this.$store.state.auth.isLoading = true;
        this.$store.dispatch(DOWNLOAD_SITE_DOCUMENTS, {selectedIds})
          .then(response => {
            return response;
          })
          .catch(error => {
            this.$notify({
              title: 'Error',
              text: error,
              type: 'error'
            });
          }).finally(() => {
            this.$store.state.auth.isLoading = false;
          });
      },
      downloadSelectedReceived() {
        let selectedRows = this.$refs.receivedDocumentTable.$refs.table.selectedRows;
        if(selectedRows.length > 0){
            this.downloadSelected(selectedRows);
        }
      },
      downloadSelectedSubmitted() {
        let selectedRows = this.$refs.submittedDocumentTable.$refs.table.selectedRows;
          if(selectedRows.length > 0){
              this.downloadSelected(selectedRows);
          }
      },
      formatJobs: function (list) {
        list.map((job) => {
          let job_number = {};
          let type = {};
          let task = {};
          let status = {};
          let stage = {};
          let visit_date = {};
          let lead_assessor_name = {};
          let lead_assessor_email = {};

          job_number.value = job.job_number;
          job_number.link = '/job/' + job.id;
          type.value = job.type;
          task.value = job.job_details;
          status.value = typeof(job.status) !== 'undefined' ? job.status : '-';
          stage.value = job.stage;
          visit_date.value = formatDate(job.visit_date, constants.DD_MM_YYYY);
          status.type = job.status === 'In Progress' ? 'accent' : null;
          if(job.lead_assessor) {
            job.lead_assessor = JSON.parse(job.lead_assessor);
            lead_assessor_name.value = job.lead_assessor.first_name + ' ' + job.lead_assessor.last_name
            lead_assessor_email.value = job.lead_assessor.email;
          }
          job.job_number = job_number;
          job.type = type;
          job.task = task;
          job.status = status;
          job.stage = stage;
          job.visit_date = visit_date;
          job.lead_assessor_name = lead_assessor_name;
          job.lead_assessor_email = lead_assessor_email;
        });
        return list;
      },

      openUploadModal() {
        this.showUploadModal = true;
      },
      closeUploadModal() {
        this.file = null;
        this.showUploadModal = false;
        this.fileConfirmed = false;
        this.description = '';
        this.uploadDescription = '';
      },
      fileAdded(file) {
        this.file = file;
      },
      fileRemoved(file) {
        this.fileConfirmed = false;
        this.file = null;
      },
      previewDoc(params) {
        // If Microsoft Edge below 50 version
        if(window.navigator && window.navigator.msSaveOrOpenBlob) {
            this.downloadDoc(params);
            return;
        }
        let url = params;
        this.$store.dispatch(PREVIEW_SITE_DOCUMENT, {url})
          .then(response => {
              window.open(response, '_blank');
          })
          .catch(error => {
            this.$notify({
              title: 'Error',
              text: error,
              type: 'error'
            });
          })
      },
      changeColValue(data) {
        let temp = this.editData;

        temp.editableCols.map(function (col) {
          if (data.id == col.key) {
            col.value = data.input;
          }
        });
        this.editedData = temp;
        this.dataIsChanged = true;
      },
      uploadDocument: function () {
        this.isUploading = true;
        let file = this.file;
        let siteId = this.siteId;
        let description = this.uploadDescription;
        this.$store.dispatch(UPLOAD_SITE_DOCUMENT, {file, siteId, description})
          .then(response => {
            this.isUploading = false;
            this.getDocuments(this.siteId);
            this.closeUploadModal();
            this.uploadDescription = '';
          })
          .catch(error => {
            this.isUploading = false;
            this.description = '';
            this.uploadDescription = '';
            this.$notify({
              title: 'Error',
              text: error,
              type: 'error'
            });
          });
      },
      uploadError: function (errors) {
        for (var i = errors.length - 1; i >= 0; i--) {
          this.$notify({
            title: 'Error',
            text: errors[i],
            type: 'error'
          });
        }
      },
    }
  }
</script>

